import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import Player from '@vimeo/player'
import Image from 'next/image'
import * as Dialog from '@radix-ui/react-dialog'
import Carousel, { ArrowProps } from 'react-multi-carousel'
import 'react-multi-carousel/lib/styles.css'

import { DialogOverlay, DialogContent, DialogTitle } from '../../Dialog'
import { IVideoContent } from './Video'

import { PlayIcon, ChevronIcon } from 'icons'

import { useTracking } from '../../../services/context/TrackingContext'

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 640 },
    items: 2,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 640, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
}

const CustomLeftArrow = ({ onClick }: ArrowProps) => (
  <button
    onClick={onClick}
    className="absolute left-8 top-[40%] z-20 -translate-y-1/2 rounded-full bg-white/80 p-10 backdrop-blur-sm transition-all hover:bg-white focus:outline-none"
    aria-label="Previous"
  >
    <ChevronIcon className="h-24 w-24 rotate-90 stroke-teal-3" />
  </button>
)

const CustomRightArrow = ({ onClick }: ArrowProps) => (
  <button
    onClick={onClick}
    className="absolute right-8 top-[40%] z-20 -translate-y-1/2 rounded-full bg-white/80 p-10 backdrop-blur-sm transition-all hover:bg-white focus:outline-none"
    aria-label="Next"
  >
    <ChevronIcon className="h-24 w-24 -rotate-90 stroke-teal-3" />
  </button>
)

export interface IVideoGridContent {
  heading: string
  description: string
  videosCollection: {
    items: IVideoContent[]
  }
}

interface IVideoProps {
  content: IVideoGridContent
}

const Video = ({ videoId }: { videoId: number }) => {
  const { track } = useTracking()
  const playerRef = useRef<Player | null>(null)
  const initialPlayRef = useRef(true)

  const trackVideo = useCallback(
    ({ event, seconds, duration, volume, title }) => {
      track({
        event,
        properties: {
          position: seconds,
          total_length: duration,
          sound: volume,
          title,
          video_player: 'Vimeo',
        },
      })
    },
    [track]
  )

  const loadAndPlayVideo = useCallback(
    async (videoId: number) => {
      playerRef.current = new Player(`video-grid-${videoId}`, {
        id: videoId,
        width: 640,
      })
      const title = await playerRef.current?.getVideoTitle()

      playerRef.current.on('play', async (event) => {
        try {
          if (event) {
            const { seconds, duration } = event // conditionally destructure because play event can be undefined
            const volume = await playerRef.current?.getVolume()

            trackVideo({
              event: initialPlayRef.current
                ? 'Video Playback Started'
                : 'Video Playback Resumed',
              title,
              duration,
              seconds,
              volume,
            })

            if (initialPlayRef.current) {
              trackVideo({
                event: 'Video Content Started',
                duration,
                seconds,
                volume,
                title,
              })
              initialPlayRef.current = false
            }
          }
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        } catch (error: any) {
          throw new Error(error)
        }
      })

      playerRef.current.on('ended', async ({ seconds, duration }) => {
        try {
          const volume = await playerRef.current?.getVolume()
          trackVideo({
            event: 'Video Playback Completed',
            title,
            seconds,
            duration,
            volume,
          })
          trackVideo({
            event: 'Video Content Completed',
            title,
            seconds,
            duration,
            volume,
          })
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        } catch (error: any) {
          throw new Error(error)
        }
      })

      playerRef.current.on('pause', async ({ seconds, duration, percent }) => {
        try {
          const volume = await playerRef.current?.getVolume()
          if (percent !== 1) {
            trackVideo({
              event: 'Video Playback Paused',
              title,
              seconds,
              duration,
              volume,
            })
          }
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        } catch (error: any) {
          throw new Error(error)
        }
      })

      playerRef.current.play()
    },
    [trackVideo]
  )

  useEffect(() => {
    loadAndPlayVideo(videoId)
  }, [loadAndPlayVideo, videoId])

  return <div id={`video-grid-${videoId}`} className="absolute inset-0"></div>
}

const VideoGrid = ({ content }: IVideoProps) => {
  const [activeVideo, setActiveVideo] = useState<IVideoContent | null>(null)
  const { t } = useTranslation('common')

  // Generate slug from heading for anchor links
  const generateSlug = (heading: string) => {
    return heading
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  // Auto-open video if URL hash matches a testimonial
  // Supports two formats:
  // - #testimonial-{slug} (SEO-friendly, e.g. #testimonial-tim-and-jamie)
  // - #v{number} (short, e.g. #v1, #v2 - for tweets/SMS)
  useEffect(() => {
    const openVideoFromHash = () => {
      const hash = window.location.hash
      const items = content.videosCollection.items

      let video: IVideoContent | undefined

      if (hash.startsWith('#testimonial-')) {
        const slug = hash.replace('#testimonial-', '')
        video = items.find((v) => generateSlug(v.heading) === slug)
      } else if (hash.match(/^#v\d+$/)) {
        const index = parseInt(hash.replace('#v', '')) - 1
        video = items[index]
      }

      if (video) {
        const matchedVideo = video
        // Small delay to ensure page has scrolled to element first
        setTimeout(() => {
          setActiveVideo(matchedVideo)
        }, 500)
      }
    }

    // Run on mount
    openVideoFromHash()

    // Listen for hash changes
    window.addEventListener('hashchange', openVideoFromHash)
    return () => window.removeEventListener('hashchange', openVideoFromHash)
  }, [content.videosCollection.items])

  return (
    <Dialog.Root open={!!activeVideo} onOpenChange={() => setActiveVideo(null)}>
      <section id="testimonials" className="rounded bg-white p-16 sm:p-48">
        {content.heading || content.description ? (
          <div className="mb-32 space-y-8 text-center">
            {content.heading ? (
              <h2 className="hd-xl">{content.heading}</h2>
            ) : null}
            {content.description ? (
              <p className="hd-md">{content.description}</p>
            ) : null}
          </div>
        ) : null}
        <div className="relative px-48">
          <Carousel
            responsive={responsive}
            arrows={true}
            customLeftArrow={<CustomLeftArrow />}
            customRightArrow={<CustomRightArrow />}
            itemClass="px-8"
            containerClass="-mx-8"
          >
            {content.videosCollection.items.map((video) => (
              <div
                key={video.videoId}
                id={`testimonial-${generateSlug(video.heading)}`}
                className="h-full"
              >
                <div className="relative aspect-video">
                  <Image
                    src={video.image.url}
                    className="rounded"
                    objectFit="cover"
                    layout="fill"
                    alt=""
                  />
                  <button
                    className="group absolute left-0 top-0 z-20 grid h-full w-full place-content-center"
                    onClick={() => setActiveVideo(video)}
                  >
                    <div className="grid h-60 w-60 place-content-center rounded-full bg-white transition-transform duration-300 group-hover:scale-110">
                      <span className="sr-only">{t('ui.play')}</span>
                      <PlayIcon
                        aria-hidden="true"
                        className="h-18 w-14 stroke-teal-3"
                      />
                    </div>
                  </button>
                </div>
                <p className="mt-16 font-semibold">{video.heading}</p>
                <p className="mt-4 body">{video.description}</p>
              </div>
            ))}
          </Carousel>
        </div>
      </section>
      <AnimatePresence>
        <Dialog.Portal>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ type: 'tween', duration: 0.2, ease: 'easeInOut' }}
            className="relative z-modal"
          >
            <DialogOverlay>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  type: 'tween',
                  duration: 0.2,
                  ease: 'easeInOut',
                  delay: 0.2,
                }}
                className="relative z-modal w-full max-w-800"
              >
                <DialogContent className="w-full overflow-hidden">
                  <DialogTitle>{activeVideo?.heading}</DialogTitle>
                  <div className="iframe-child:absoltue relative aspect-video iframe-child:left-0 iframe-child:top-0 iframe-child:h-full iframe-child:w-full">
                    {activeVideo ? (
                      <Video videoId={activeVideo.videoId} />
                    ) : null}
                  </div>
                </DialogContent>
              </motion.div>
            </DialogOverlay>
          </motion.div>
        </Dialog.Portal>
      </AnimatePresence>
    </Dialog.Root>
  )
}

export default VideoGrid
